import { useTenantTheme } from '../useTenantTheme.js';

/**
 * Visual stand-ins for Spotlight content surfaces.
 *
 * Renders a non-attached mockup of whatever the SDK would pop on
 * a real page — tooltip, modal, banner, hotspot, spotlight, tour
 * step, or the start/finish page of a tour (which the SDK renders
 * as a centred modal).  Everything is wrapped in ``<ThemeScope>``
 * so the tenant's active theme variables apply — edit a theme,
 * open a preview, see the change.
 */

/**
 * Wrap preview content so the tenant's default theme `--spot-*`
 * variables apply to everything inside.  Children's CSS still
 * references the same var names; ThemeScope just populates them
 * from the live theme instead of falling back to constants.
 */
export function ThemeScope({ children, className = '' }) {
  const { style } = useTenantTheme();
  return (
    <div className={`spotlight-preview-scope ${className}`.trim()} style={style}>
      {children}
    </div>
  );
}

export function ContentPreview({ item }) {
  if (!item) return null;
  const type = (item.content_type || 'tooltip').toLowerCase();

  if (type === 'banner') return <BannerPreview item={item} />;
  if (type === 'modal') return <ModalPreview item={item} />;
  if (type === 'hotspot') return <HotspotPreview item={item} />;
  if (type === 'spotlight') return <SpotlightPreview item={item} />;
  return <TooltipPreview item={item} />;
}

export function TourStepPreview({ step, index }) {
  if (!step) return null;
  return (
    <div className="spotlight-preview">
      <div className="spotlight-preview__stage">
        <div className="spotlight-preview__page">
          <div className="spotlight-preview__page-line" style={{ width: '60%' }} />
          <div className="spotlight-preview__page-line" style={{ width: '85%' }} />
          <div
            className="spotlight-preview__target"
            data-tour-selector={step.element_target?.selector || ''}
          >
            {step.element_target?.selector ? (
              <code>{step.element_target.selector}</code>
            ) : 'Target element'}
          </div>
          <div className="spotlight-preview__page-line" style={{ width: '70%' }} />
        </div>
        <div className="spotlight-preview__tooltip">
          <div className="spotlight-preview__tooltip-label">
            Step {(step.order ?? index) + 1}
            {step.optional && <span className="badge badge--default">optional</span>}
          </div>
          <strong>{step.title || 'Untitled step'}</strong>
          {step.body && <p>{step.body}</p>}
          <div className="spotlight-preview__tooltip-actions">
            <button type="button" className="spotlight-preview__skip">Skip</button>
            <button type="button" className="spotlight-preview__primary">
              {step.cta_text || (index === 0 ? 'Next' : 'Continue')}
            </button>
          </div>
        </div>
      </div>
      <style>{PREVIEW_CSS}</style>
    </div>
  );
}

function TooltipPreview({ item }) {
  return (
    <div className="spotlight-preview">
      <div className="spotlight-preview__stage">
        <div className="spotlight-preview__page">
          <div className="spotlight-preview__page-line" style={{ width: '60%' }} />
          <div className="spotlight-preview__target">Target element</div>
          <div className="spotlight-preview__page-line" style={{ width: '70%' }} />
        </div>
        <div className="spotlight-preview__tooltip">
          <strong>{item.title}</strong>
          {item.body && <p>{item.body}</p>}
          {item.cta_text && (
            <div className="spotlight-preview__tooltip-actions">
              <button type="button" className="spotlight-preview__primary">
                {item.cta_text}
              </button>
            </div>
          )}
        </div>
      </div>
      <style>{PREVIEW_CSS}</style>
    </div>
  );
}

function ModalPreview({ item }) {
  return (
    <div className="spotlight-preview spotlight-preview--modal">
      <div className="spotlight-preview__backdrop">
        <div className="spotlight-preview__modal">
          <div className="spotlight-preview__modal-head">
            <strong>{item.title}</strong>
            <button type="button" aria-label="Close">✕</button>
          </div>
          {item.body && <div className="spotlight-preview__modal-body">{item.body}</div>}
          {item.cta_text && (
            <div className="spotlight-preview__tooltip-actions" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="spotlight-preview__skip">Dismiss</button>
              <button type="button" className="spotlight-preview__primary">{item.cta_text}</button>
            </div>
          )}
        </div>
      </div>
      <style>{PREVIEW_CSS}</style>
    </div>
  );
}

function BannerPreview({ item }) {
  const position = item.banner_placement?.position || 'top';
  return (
    <div className={`spotlight-preview spotlight-preview--banner spotlight-preview--banner-${position}`}>
      <div className="spotlight-preview__stage">
        {position === 'top' && <BannerBar item={item} />}
        <div className="spotlight-preview__page">
          <div className="spotlight-preview__page-line" style={{ width: '60%' }} />
          <div className="spotlight-preview__page-line" style={{ width: '85%' }} />
          <div className="spotlight-preview__page-line" style={{ width: '70%' }} />
        </div>
        {position !== 'top' && <BannerBar item={item} />}
      </div>
      <style>{PREVIEW_CSS}</style>
    </div>
  );
}

function BannerBar({ item }) {
  return (
    <div className="spotlight-preview__banner">
      <div>
        <strong>{item.title}</strong>
        {item.body && <span> — {item.body}</span>}
      </div>
      <div className="spotlight-preview__tooltip-actions" style={{ margin: 0 }}>
        {item.cta_text && (
          <button type="button" className="spotlight-preview__primary">{item.cta_text}</button>
        )}
        <button type="button" className="spotlight-preview__skip">✕</button>
      </div>
    </div>
  );
}

function HotspotPreview({ item }) {
  return (
    <div className="spotlight-preview">
      <div className="spotlight-preview__stage">
        <div className="spotlight-preview__page">
          <div className="spotlight-preview__page-line" style={{ width: '60%' }} />
          <div className="spotlight-preview__target spotlight-preview__target--hotspot">
            Target element
            <span className="spotlight-preview__hotspot-dot" aria-hidden="true" />
          </div>
          <div className="spotlight-preview__page-line" style={{ width: '70%' }} />
        </div>
        <div className="spotlight-preview__tooltip">
          <strong>{item.title}</strong>
          {item.body && <p>{item.body}</p>}
          {item.cta_text && (
            <div className="spotlight-preview__tooltip-actions">
              <button type="button" className="spotlight-preview__primary">
                {item.cta_text}
              </button>
            </div>
          )}
        </div>
      </div>
      <style>{PREVIEW_CSS}</style>
    </div>
  );
}

function SpotlightPreview({ item }) {
  return (
    <div className="spotlight-preview">
      <div className="spotlight-preview__stage">
        <div className="spotlight-preview__page spotlight-preview__page--dim">
          <div className="spotlight-preview__page-line" style={{ width: '60%' }} />
          <div className="spotlight-preview__target spotlight-preview__target--lit">
            Target element
          </div>
          <div className="spotlight-preview__page-line" style={{ width: '70%' }} />
        </div>
        <div className="spotlight-preview__tooltip">
          <strong>{item.title}</strong>
          {item.body && <p>{item.body}</p>}
          {item.cta_text && (
            <div className="spotlight-preview__tooltip-actions">
              <button type="button" className="spotlight-preview__primary">
                {item.cta_text}
              </button>
            </div>
          )}
        </div>
      </div>
      <style>{PREVIEW_CSS}</style>
    </div>
  );
}

export function ChecklistPreview({ checklist }) {
  const items = checklist?.items || [];
  return (
    <div className="spotlight-preview">
      <div className="spotlight-preview__checklist">
        <div className="spotlight-preview__checklist-head">
          <strong>{checklist?.title || 'Checklist'}</strong>
          {checklist?.description && <p>{checklist.description}</p>}
        </div>
        <ul>
          {items.length === 0 ? (
            <li className="spotlight-preview__checklist-empty">
              (no items yet)
            </li>
          ) : (
            items.map((it, i) => (
              <li key={it.item_id || i}>
                <span
                  className={`spotlight-preview__check${i === 0 ? ' spotlight-preview__check--done' : ''}`}
                  aria-hidden="true"
                />
                <div>
                  <strong>{it.title || it.label || 'Untitled item'}</strong>
                  {it.description && <div>{it.description}</div>}
                  {(it.action_url || it.completion_event) && (
                    <div className="spotlight-preview__checklist-meta">
                      {it.action_url && (
                        <span>
                          goes to <code>{it.action_url}</code>
                        </span>
                      )}
                      {it.completion_event && (
                        <span>
                          completes on <code>{it.completion_event}</code>
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
      <style>{PREVIEW_CSS}</style>
    </div>
  );
}

const PREVIEW_CSS = `
  .spotlight-preview {
    --spot-bg-fallback: var(--spot-bg, #ffffff);
    --spot-text-fallback: var(--spot-text, #111827);
    --spot-primary-fallback: var(--spot-primary, #4f46e5);
    --spot-primary-text-fallback: var(--spot-primary-text, #ffffff);
    --spot-radius-fallback: var(--spot-radius, 8px);
    --spot-border-fallback: var(--spot-border, #e5e7eb);
    --spot-shadow-fallback: var(--spot-shadow-lg, 0 10px 15px -3px rgba(0,0,0,0.1));
  }
  .spotlight-preview__stage {
    position: relative;
    background: var(--surface-1);
    border: 1px solid var(--border-1);
    border-radius: var(--r-lg);
    padding: 20px;
    min-height: 220px;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 20px;
    align-items: center;
  }
  .spotlight-preview__page {
    background: var(--surface-0);
    border: 1px solid var(--border-1);
    border-radius: var(--r-md);
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 160px;
  }
  .spotlight-preview__page--dim { background: rgba(15, 23, 42, 0.55); }
  .spotlight-preview__page--dim .spotlight-preview__page-line { background: rgba(255,255,255,0.1); }
  .spotlight-preview__page-line {
    height: 6px;
    border-radius: 3px;
    background: var(--border-1);
  }
  .spotlight-preview__target {
    background: var(--surface-muted);
    border: 1px dashed var(--border-2);
    border-radius: var(--r-sm);
    padding: 10px 12px;
    align-self: flex-start;
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--text-3);
    position: relative;
  }
  .spotlight-preview__target--lit {
    background: rgba(79, 70, 229, 0.12);
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.3);
    color: var(--text-1);
    border-color: transparent;
  }
  .spotlight-preview__target--hotspot {
    padding-right: 34px;
  }
  .spotlight-preview__hotspot-dot {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: var(--spot-primary-fallback);
    box-shadow: 0 0 0 6px rgba(79, 70, 229, 0.2);
    animation: spotlight-pulse 1.8s ease-in-out infinite;
  }
  @keyframes spotlight-pulse {
    0%, 100% { box-shadow: 0 0 0 6px rgba(79, 70, 229, 0.2); }
    50%      { box-shadow: 0 0 0 12px rgba(79, 70, 229, 0); }
  }
  .spotlight-preview__tooltip {
    background: var(--spot-bg-fallback);
    color: var(--spot-text-fallback);
    border: 1px solid var(--spot-border-fallback);
    border-radius: var(--spot-radius-fallback);
    box-shadow: var(--spot-shadow-fallback);
    padding: 14px 16px;
    max-width: 320px;
    font-size: 13.5px;
    line-height: 1.45;
  }
  .spotlight-preview__tooltip strong { display: block; margin-bottom: 6px; }
  .spotlight-preview__tooltip p { margin: 0 0 10px; color: var(--text-2); }
  .spotlight-preview__tooltip-label {
    display: flex;
    gap: 6px;
    align-items: center;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-3);
    margin-bottom: 6px;
  }
  .spotlight-preview__tooltip-actions {
    display: flex;
    gap: 8px;
    margin-top: 10px;
    align-items: center;
    justify-content: flex-end;
  }
  .spotlight-preview__primary {
    background: var(--spot-primary-fallback);
    color: var(--spot-primary-text-fallback);
    border: 0;
    padding: 7px 14px;
    border-radius: var(--spot-radius-fallback);
    font-weight: 600;
    font-size: 13px;
    cursor: default;
  }
  .spotlight-preview__skip {
    background: transparent;
    border: 1px solid var(--border-2);
    color: var(--text-3);
    padding: 6px 12px;
    border-radius: var(--spot-radius-fallback);
    font-size: 13px;
    cursor: default;
  }

  /* Modal */
  .spotlight-preview--modal .spotlight-preview__backdrop {
    background: rgba(15, 23, 42, 0.4);
    padding: 40px 20px;
    border-radius: var(--r-lg);
    display: grid;
    place-items: center;
  }
  .spotlight-preview__modal {
    background: var(--spot-bg-fallback);
    border-radius: var(--spot-radius-fallback);
    box-shadow: var(--spot-shadow-fallback);
    padding: 22px 24px;
    max-width: 420px;
    width: 100%;
  }
  .spotlight-preview__modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }
  .spotlight-preview__modal-head button {
    background: transparent;
    border: 0;
    color: var(--text-3);
    font-size: 14px;
    cursor: default;
  }

  /* Banner */
  .spotlight-preview--banner .spotlight-preview__stage {
    display: block;
    padding: 0;
    overflow: hidden;
  }
  .spotlight-preview--banner .spotlight-preview__page {
    margin: 18px 18px;
  }
  .spotlight-preview__banner {
    background: var(--spot-primary-fallback);
    color: var(--spot-primary-text-fallback);
    padding: 10px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
  }
  .spotlight-preview__banner strong { margin-right: 4px; }
  .spotlight-preview--banner-top .spotlight-preview__banner { border-radius: var(--r-lg) var(--r-lg) 0 0; }
  .spotlight-preview--banner-bottom .spotlight-preview__banner { border-radius: 0 0 var(--r-lg) var(--r-lg); }

  /* Checklist */
  .spotlight-preview__checklist {
    background: var(--spot-bg-fallback);
    border: 1px solid var(--spot-border-fallback);
    border-radius: var(--spot-radius-fallback);
    box-shadow: var(--spot-shadow-fallback);
    padding: 18px 20px;
    max-width: 380px;
    font-size: 13.5px;
  }
  .spotlight-preview__checklist-head { margin-bottom: 10px; }
  .spotlight-preview__checklist-head strong { display: block; }
  .spotlight-preview__checklist-head p {
    margin: 4px 0 0;
    color: var(--text-3);
    font-size: 13px;
  }
  .spotlight-preview__checklist ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .spotlight-preview__checklist li {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 8px 10px;
    border-radius: var(--r-md);
    background: var(--surface-1);
  }
  .spotlight-preview__checklist li strong { display: block; }
  .spotlight-preview__checklist li div:last-child { font-size: 12.5px; color: var(--text-3); }
  .spotlight-preview__check {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid var(--border-2);
    flex: 0 0 18px;
    margin-top: 2px;
  }
  .spotlight-preview__check--done {
    background: var(--spot-primary-fallback);
    border-color: var(--spot-primary-fallback);
    position: relative;
  }
  .spotlight-preview__check--done::after {
    content: "";
    position: absolute;
    left: 4px;
    top: 1px;
    width: 5px;
    height: 10px;
    border: 2px solid var(--spot-primary-text-fallback);
    border-top: 0;
    border-left: 0;
    transform: rotate(45deg);
  }
  .spotlight-preview__checklist-empty {
    color: var(--text-3);
    font-style: italic;
    padding: 10px;
    background: var(--surface-1);
    border-radius: var(--r-md);
  }
  .spotlight-preview__checklist-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    font-size: 12px;
    color: var(--text-3);
    margin-top: 4px;
  }
  .spotlight-preview__checklist-meta code {
    font-family: var(--font-mono);
    font-size: 11.5px;
  }

  /* ThemeScope applies theme vars on its wrapper so children
     inherit via custom-property resolution.  No positioning
     beyond that — layout lives in the consuming page. */
  .spotlight-preview-scope {
    display: contents;
  }
`;
